// components/AuthScreen.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  signIn, signUp,
  requestMagicLink, verifyOTP,
  forgotPassword, resetPassword,
  loginWithGoogle,
  handleOAuthCallback,
  AuthUser,
} from '../utils/authApi';

// ─── Types ────────────────────────────────────────────────────────────────────
type Tab   = 'signin' | 'signup' | 'magic';
type Stage = 'form' | 'otp';

interface Props {
  onAuth: (user: AuthUser, token: string) => void;
  onClose?: () => void;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const GoogleIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const OrgIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const Spinner = () => (
  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
  </svg>
);

// ─── Shared input class ───────────────────────────────────────────────────────
const inputCls =
  'w-full bg-white border border-slate-200/90 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 font-medium placeholder:text-slate-400 focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 transition-all shadow-sm';

const labelCls = 'text-xs font-semibold text-slate-700 block mb-1.5';
const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

function CashierScanLogin({ onAuth, onClose }: { onAuth: (user: AuthUser, token: string) => void; onClose: () => void }) {
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    let interval: any;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'environment' } })
      .then(stream => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setScanning(true);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        interval = setInterval(() => {
          const v = videoRef.current;
          if (!v || v.readyState < 2) return;
          canvas.width = v.videoWidth;
          canvas.height = v.videoHeight;
          ctx.drawImage(v, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          
          const jsQR = (window as any).jsQR;
          if (!jsQR) return;
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          if (code?.data?.startsWith('CASHIER_TOKEN:')) {
            clearInterval(interval);
            stopStream();
            const token = code.data.replace('CASHIER_TOKEN:', '');
            handleTokenLogin(token);
          }
        }, 300);
      })
      .catch(() => setError('Camera access denied.'));

    return () => { clearInterval(interval); stopStream(); };
  }, []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
  };

  const handleTokenLogin = async (badgeToken: string) => {
    try {
      const loginRes = await fetch(`${BACKEND}/auth/cashier-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ badge_token: badgeToken }),
      });
      if (!loginRes.ok) {
        const err = await loginRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Invalid or expired badge.');
      }
      const { access_token } = await loginRes.json();

      const meRes = await fetch(`${BACKEND}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      if (!meRes.ok) throw new Error('Badge login failed.');
      const user: AuthUser = await meRes.json();
      onAuth(user, access_token);
    } catch (e: any) {
      setError(e.message || 'Badge login failed.');
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-6 w-full max-w-xs text-center shadow-2xl border border-slate-100">
        <div className="font-extrabold text-slate-900 text-base mb-1">📷 Scan Cashier Badge</div>
        <div className="text-xs text-slate-500 mb-4">Point your camera directly at the QR badge</div>
        <div className="rounded-2xl overflow-hidden bg-black mb-4 relative h-56">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          {scanning && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-36 h-36 border-2 border-blue-500 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.5)] animate-pulse" />
            </div>
          )}
        </div>
        {error && <div className="bg-red-50 text-red-600 rounded-xl p-2.5 text-xs font-medium mb-3">{error}</div>}
        <button 
          onClick={() => { stopStream(); onClose(); }}
          className="w-full py-2.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 font-bold text-xs text-slate-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Human-readable error translator ──────────────────────────────────────────
function formatAuthError(error: any): string {
  if (!error) return 'An unexpected error occurred. Please try again.';

  let raw = '';
  if (typeof error === 'string') {
    raw = error;
  } else if (error.detail) {
    if (Array.isArray(error.detail)) {
      raw = error.detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
    } else {
      raw = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
    }
  } else if (error.message) {
    raw = error.message;
  } else {
    try {
      raw = JSON.stringify(error);
    } catch {
      raw = String(error);
    }
  }

  // Handle nested JSON strings like '{"detail":"..."}'
  if (raw.startsWith('{') && raw.endsWith('}')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed.detail) return formatAuthError(parsed.detail);
      if (parsed.message) return formatAuthError(parsed.message);
    } catch {}
  }

  const lower = raw.toLowerCase();

  // 1. Google / OAuth account trying to log in with password
  if (
    lower.includes('google') ||
    lower.includes('oauth') ||
    lower.includes('social') ||
    lower.includes('no password') ||
    lower.includes('password not set')
  ) {
    return "This account was created using Google. Please click 'Continue with Google' below to sign in.";
  }

  // 2. Invalid credentials (also gently reminds about Google)
  if (
    lower.includes('invalid credentials') ||
    lower.includes('incorrect password') ||
    lower.includes('wrong password') ||
    lower.includes('unauthorized') ||
    lower.includes('401')
  ) {
    return "Incorrect email or password. If you signed up with Google, please click 'Continue with Google' below.";
  }

  // 3. Email already registered
  if (lower.includes('already exists') || lower.includes('already registered')) {
    return "An account with this email already exists. Please switch to Sign In or use Google.";
  }

  // 4. User not found
  if (lower.includes('not found') || lower.includes('no user')) {
    return "No account found with this email. Please create an account first.";
  }

  // 5. Network / Server down
  if (lower.includes('failed to fetch') || lower.includes('network') || lower.includes('connection')) {
    return "Unable to reach server. Please check your internet connection.";
  }

  // Clean fallback: strip technical 'Error: ' prefix
  return raw.replace(/^Error:\s*/i, '').trim();
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function AuthScreen({ onAuth, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('signin');
  const [stage, setStage] = useState<Stage>('form');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [accountType, setAccountType] = useState<'individual' | 'organization'>('individual');
  const [companyName, setCompanyName] = useState('');

  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCashierScan, setShowCashierScan] = useState(false);

  // ── Forgot password ────────────────────────────────────────────────────────
  const [forgotStage, setForgotStage] = useState<'closed' | 'email' | 'reset'>('closed');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotInfo, setForgotInfo] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setForgotError('');
    setForgotLoading(true);
    try {
      await forgotPassword(forgotEmail.trim());
      setResendCooldown(30);
      setForgotInfo('A new code has been sent to your email.');
    } catch (e: any) {
      setForgotError(e.message || 'Something went wrong.');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgot = () => {
    setForgotStage('closed');
    setForgotEmail('');
    setForgotOtp('');
    setForgotNewPassword('');
    setShowForgotNewPassword(false);
    setForgotError('');
    setForgotInfo('');
  };

  const handleForgotSubmit = async () => {
    setForgotError('');
    setForgotInfo('');

    if (forgotStage === 'email') {
      if (!forgotEmail.trim()) { setForgotError('Email is required.'); return; }
      setForgotLoading(true);
      try {
        await forgotPassword(forgotEmail.trim());
        setForgotStage('reset');
        setForgotInfo(`If ${forgotEmail.trim()} is registered, a code was sent.`);
        setResendCooldown(30);
      } catch (e: any) {
        setForgotError(e.message || 'Something went wrong.');
      } finally {
        setForgotLoading(false);
      }
    } else if (forgotStage === 'reset') {
      if (forgotOtp.length !== 6) { setForgotError('Enter the 6-digit code.'); return; }
      if (forgotNewPassword.length < 8) { setForgotError('Password must be at least 8 characters.'); return; }
      setForgotLoading(true);
      try {
        await resetPassword(forgotEmail.trim(), forgotOtp.trim(), forgotNewPassword);
        closeForgot();
        setTab('signin');
        setEmail(forgotEmail.trim());
        setPassword('');
        setInfo('Password reset — sign in with your new password.');
      } catch (e: any) {
        setForgotError(e.message || 'Something went wrong.');
      } finally {
        setForgotLoading(false);
      }
    }
  };

  // Direct authentication — no blocking popup needed
  const completeAuth = (user: AuthUser, token: string) => {
    onAuth(user, token);
  };

  // ── OAuth Callback ─────────────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('access_token')) {
      setLoading(true);
      handleOAuthCallback()
        .then(result => {
          if (result) completeAuth(result.user, result.access_token);
        })
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, []);

  const reset = (newTab: Tab) => {
    setTab(newTab);
    setStage('form');
    setError('');
    setInfo('');
    setOtp('');
    setShowPassword(false);
  };

  // ── Submit handler ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError('');
    setInfo('');

    if (tab !== 'magic' && !password) { setError('Password is required.'); return; }
    if (tab === 'signup' && !name.trim()) { setError('Name is required.'); return; }
    if (!email.trim()) { setError('Email is required.'); return; }
    if (tab !== 'magic' && password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (tab === 'magic' && stage === 'otp' && otp.length !== 6) { setError('Enter the 6-digit code.'); return; }

    setLoading(true);
    try {
      if (tab === 'signup') {
        const result = await signUp(name.trim(), email.trim(), password, accountType, companyName.trim());
        completeAuth(result.user, result.access_token);
      } else if (tab === 'signin') {
        const result = await signIn(email.trim(), password);
        completeAuth(result.user, result.access_token);
      } else if (tab === 'magic') {
        if (stage === 'form') {
          await requestMagicLink(email.trim());
          setStage('otp');
          setInfo(`Code sent to ${email.trim()} — check your inbox.`);
        } else {
          const result = await verifyOTP(email.trim(), otp.trim());
          completeAuth(result.user, result.access_token);
        }
      }
    } catch (e: any) {
      setError(formatAuthError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  // ── OTP display ────────────────────────────────────────────────────────────
  const OtpBoxes = () => {
    const digits = otp.padEnd(6, ' ');
    return (
      <div className="relative">
        <div className="flex gap-2 justify-center mb-1" aria-hidden>
          {Array.from(digits).map((d, i) => (
            <div
              key={i}
              className={`w-10 h-12 rounded-xl border-2 flex items-center justify-center text-lg font-bold transition-all ${
                d.trim()
                  ? 'border-blue-600 bg-blue-50/50 text-blue-900'
                  : 'border-slate-200 bg-slate-50/50 text-slate-300'
              }`}
            >
              {d.trim() || '·'}
            </div>
          ))}
        </div>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={otp}
          onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
          onKeyDown={handleKey}
          autoFocus
          className="absolute inset-0 opacity-0 cursor-text"
          aria-label="One-time code"
        />
      </div>
    );
  };

  const heading = tab === 'signup'
    ? 'Create your account'
    : tab === 'signin'
    ? 'Welcome back'
    : stage === 'otp'
    ? 'Check your inbox'
    : 'Sign in with email';

  const subheading = tab === 'signup'
    ? 'Start generating professional invoices today'
    : tab === 'signin'
    ? 'Enter your credentials to continue'
    : stage === 'otp'
    ? `We sent a 6-digit code to ${email}`
    : "We'll send a one-time code to your inbox";

  return (
    <div 
      className="min-h-screen bg-slate-50 flex items-center justify-center p-4"
      style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      <div className="w-full max-w-[410px] relative">

        {/* Brand Header */}
        <div className="text-center mb-7">
          <div className="inline-flex items-center justify-center mb-2.5 transition-transform hover:scale-105 duration-200">
            <img 
              src="/logo-icon.png" 
              alt="CloudAIRambo" 
              className="h-20 w-auto max-w-[200px] object-contain drop-shadow-[0_8px_24px_rgba(37,99,235,0.25)]" 
            />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            CloudAIRambo
          </h1>
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1">
            Professional Invoice & POS Platform
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 p-6 sm:p-7 relative">

          {/* Close button inside card */}
          {onClose && (
            <button
              onClick={() => { window.location.href = '/'; }}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title="Close"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* Segmented Pill Tabs */}
          {stage !== 'otp' && (
            <div className="grid grid-cols-2 p-1 bg-slate-100 rounded-xl mb-5">
              {(['signin', 'signup'] as Tab[]).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => reset(t)}
                  className={`py-2 text-xs font-semibold rounded-lg transition-all ${
                    tab === t
                      ? 'bg-white text-slate-900 shadow-sm font-bold'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {t === 'signin' ? 'Sign In' : 'Create Account'}
                </button>
              ))}
            </div>
          )}

          {/* Headings */}
          <div className="mb-5">
            <h2 className="text-base font-extrabold text-slate-900 tracking-tight">{heading}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{subheading}</p>
          </div>

          <div className="space-y-4">

            {tab === 'magic' && stage === 'otp' ? (
              <div className="space-y-3">
                <OtpBoxes />
                <button
                  onClick={() => { setStage('form'); setOtp(''); setError(''); setInfo(''); }}
                  className="w-full text-center text-xs text-slate-400 hover:text-blue-600 transition-colors"
                >
                  ← Change email address
                </button>
              </div>
            ) : (
              <div className="space-y-3.5">
                {tab === 'signup' && (
                  <>
                    {/* Account Type Selector */}
                    <div>
                      <label className={labelCls}>Account Type</label>
                      <div className="grid grid-cols-2 gap-2">
                        {(['individual', 'organization'] as const).map(type => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setAccountType(type)}
                            className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl border text-xs font-semibold transition-all ${
                              accountType === type
                                ? 'border-blue-600 bg-blue-50/50 text-blue-700 shadow-sm'
                                : 'border-slate-200 hover:border-slate-300 bg-white text-slate-600'
                            }`}
                          >
                            {type === 'individual' ? <UserIcon /> : <OrgIcon />}
                            <span>{type === 'individual' ? 'Individual' : 'Organization'}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Full Name</label>
                      <input
                        type="text"
                        value={name}
                        onChange={e => { setName(e.target.value); setError(''); }}
                        onKeyDown={handleKey}
                        placeholder="Rihan Rahiman"
                        autoFocus
                        className={inputCls}
                      />
                    </div>

                    {accountType === 'organization' && (
                      <div>
                        <label className={labelCls}>Company Name</label>
                        <input
                          type="text"
                          value={companyName}
                          onChange={e => { setCompanyName(e.target.value); setError(''); }}
                          onKeyDown={handleKey}
                          placeholder="Acme Corp"
                          className={inputCls}
                        />
                      </div>
                    )}
                  </>
                )}

                <div>
                  <label className={labelCls}>Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    onKeyDown={handleKey}
                    placeholder="you@example.com"
                    autoFocus={tab !== 'signup'}
                    className={inputCls}
                  />
                </div>

                {tab !== 'magic' && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className={labelCls + ' !mb-0'}>Password</label>
                      {tab === 'signin' ? (
                        <button
                          type="button"
                          onClick={() => { setForgotStage('email'); setForgotEmail(email); }}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                        >
                          Forgot password?
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-400">Min. 6 chars</span>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => { setPassword(e.target.value); setError(''); }}
                        onKeyDown={handleKey}
                        placeholder="••••••••"
                        className={`${inputCls} pr-10`}
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 focus:outline-none transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error & Info Feedback */}
            {error && (
              <div className="bg-amber-50/90 border border-amber-200 text-amber-900 text-xs font-medium px-3.5 py-2.5 rounded-xl flex items-start gap-2 shadow-sm">
                <svg className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <div className="leading-relaxed flex-1">
                  {error}
                </div>
              </div>
            )}
            {info && !error && (
              <div className="bg-emerald-50 border border-emerald-200/80 text-emerald-700 text-xs font-semibold px-3 py-2.5 rounded-xl">
                {info}
              </div>
            )}

            {/* Primary Action Button */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 active:scale-[0.99] disabled:opacity-60 text-white rounded-xl font-bold text-sm shadow-md shadow-blue-600/20 transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Spinner /><span>Please wait…</span></>
              ) : (
                <span>{tab === 'signup' ? 'Create Account' : tab === 'signin' ? 'Sign In' : stage === 'otp' ? 'Verify Code' : 'Send Code'}</span>
              )}
            </button>

            {/* Google OAuth */}
            {stage !== 'otp' && (
              <>
                <div className="flex items-center gap-3 pt-1">
                  <div className="flex-1 h-px bg-slate-100" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">or continue with</span>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const result = await loginWithGoogle();
                      completeAuth(result.user, result.access_token);
                    } catch (e: any) {
                      setError(formatAuthError(e));
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-xl border border-slate-200/90 bg-white hover:bg-slate-50 active:scale-[0.99] text-xs font-semibold text-slate-700 shadow-sm transition-all"
                >
                  <GoogleIcon /> Continue with Google
                </button>
              </>
            )}

            {/* Bottom Switch Tab */}
            {tab !== 'magic' && stage !== 'otp' && (
              <p className="text-center text-xs text-slate-500 font-medium pt-1">
                {tab === 'signin' ? "Don't have an account? " : 'Already have an account? '}
                <button
                  type="button"
                  onClick={() => reset(tab === 'signin' ? 'signup' : 'signin')}
                  className="text-blue-600 font-bold hover:text-blue-700 ml-0.5"
                >
                  {tab === 'signin' ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            )}

          </div>
        </div>

        {/* Footer links */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-[11px] text-slate-400">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>{' '}
            and{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>.
          </p>
          <p className="text-[11px] text-slate-400 font-medium">🔒 Your data is encrypted and securely stored on your backend.</p>
          <p className="text-xs text-slate-500 font-medium">
            Team member?{' '}
            <a href="/team-login" className="text-blue-600 font-bold hover:underline">
              Sign in here
            </a>
          </p>
          <button
            onClick={() => setShowCashierScan(true)}
            className="w-full py-2.5 rounded-xl border border-dashed border-slate-300 hover:border-blue-400 bg-white/60 hover:bg-blue-50/50 text-slate-600 hover:text-blue-700 text-xs font-semibold transition-all flex items-center justify-center gap-2"
          >
            📷 POS Cashier? Scan Badge to Login
          </button>
        </div>

        {showCashierScan && (
          <CashierScanLogin onAuth={completeAuth} onClose={() => setShowCashierScan(false)} />
        )}
      </div>

      {/* Forgot Password Modal */}
      {forgotStage !== 'closed' && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-sm w-full shadow-2xl border border-slate-100">
            <h3 className="font-extrabold text-slate-900 text-base mb-1">
              {forgotStage === 'email' ? 'Reset your password' : 'Enter code & new password'}
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              {forgotStage === 'email' ? "We'll send a 6-digit code to your email." : `Code sent to ${forgotEmail}`}
            </p>

            {forgotStage === 'email' ? (
              <div className="space-y-3 mb-4">
                <div>
                  <label className={labelCls}>Email Address</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={e => { setForgotEmail(e.target.value); setForgotError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleForgotSubmit(); }}
                    placeholder="you@example.com"
                    autoFocus
                    className={inputCls}
                  />
                </div>
                <div className="bg-blue-50/60 border border-blue-200/60 rounded-xl p-3 text-xs text-blue-900">
                  If you don't see the code within a couple minutes, please check your spam folder.
                </div>
              </div>
            ) : (
              <div className="space-y-3 mb-4">
                <div>
                  <label className={labelCls}>6-Digit Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={forgotOtp}
                    onChange={e => { setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setForgotError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleForgotSubmit(); }}
                    placeholder="123456"
                    autoFocus
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>New Password</label>
                  <div className="relative">
                    <input
                      type={showForgotNewPassword ? 'text' : 'password'}
                      value={forgotNewPassword}
                      onChange={e => { setForgotNewPassword(e.target.value); setForgotError(''); }}
                      onKeyDown={e => { if (e.key === 'Enter') handleForgotSubmit(); }}
                      placeholder="Min 8 characters"
                      className={`${inputCls} pr-10`}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowForgotNewPassword(!showForgotNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 focus:outline-none"
                    >
                      {showForgotNewPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs pt-1">
                  <button
                    type="button"
                    onClick={() => { setForgotStage('email'); setForgotOtp(''); setForgotNewPassword(''); setForgotError(''); setForgotInfo(''); }}
                    className="text-slate-400 hover:text-blue-600 transition-colors"
                  >
                    ← Change email
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0 || forgotLoading}
                    className={`font-bold transition-colors ${resendCooldown > 0 ? 'text-slate-400 cursor-not-allowed' : 'text-blue-600 hover:underline'}`}
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
              </div>
            )}

            {forgotError && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-xs font-semibold px-3 py-2 rounded-xl mb-3">
                {forgotError}
              </div>
            )}
            {forgotInfo && !forgotError && forgotStage === 'reset' && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-3 py-2 rounded-xl mb-3">
                {forgotInfo}
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={closeForgot}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 font-bold text-xs text-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={forgotLoading}
                onClick={handleForgotSubmit}
                className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shadow-md shadow-blue-600/20 disabled:opacity-60 transition-all"
              >
                {forgotLoading ? 'Please wait…' : forgotStage === 'email' ? 'Send Code' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consent is handled implicitly via the footer terms link */}
    </div>
  );
}
